/**
 * Learner Agent — Internacionalización (ES / EN)
 *
 * Usage:
 *   import { t, setLanguage, getLang, applyTranslations } from './i18n.js';
 *   t('new_session')       → translated string for current language
 *   setLanguage('es')      → switch UI + persist to localStorage
 *   applyTranslations()    → re-render all data-i18n elements
 */

const translations = {
    en: {
        // ── App / Header ──────────────────────────────────────────────
        app_title:              'Learner Agent',
        sign_out:               'Sign Out',
        lang_toggle_label:      'Language',

        // ── Login ──────────────────────────────────────────────────────
        login_title:            '🤖 Learner Agent',
        login_tagline:          'Teach an AI agent that starts knowing nothing',
        sign_in_google:         'Sign in with Google',

        // ── Topics list ───────────────────────────────────────────────
        teaching_sessions:      'My Teaching Topics',
        new_session:            '+ New Topic',
        empty_sessions:         'No topics yet. Start teaching something!',
        loading_sessions:       'Loading topics...',
        turns_unit:             'turns',
        concepts_unit:          'concepts',
        view:                   'View',
        continue_btn:           'Continue',
        badge_free:             'Free',
        badge_curricular:       'Curricular',

        // ── New Topic ─────────────────────────────────────────────────
        create_session_title:   'New Teaching Topic',
        back:                   '← Back',
        topic_label:            'What topic will you teach?',
        topic_placeholder:      'e.g., How to Make Pizza, Photosynthesis…',
        language_label:         'Conversation language (AI responses)',
        lang_en:                'English',
        lang_es:                'Español',
        session_type_label:     'Type',
        session_type_free:      '🔓 Free topic — teach anything',
        session_type_curricular:'📚 Curricular — guided by learning objectives',
        syllabus_label:         'Select a syllabus',
        syllabus_placeholder:   '— Choose a syllabus —',
        preview_objectives_btn: 'Preview objectives',
        start_teaching:         'Start Teaching',

        // ── Objectives modal ──────────────────────────────────────────
        objectives_title:       'Learning Objectives',
        big_ideas_title:        '💡 Big Ideas',
        objectives_list_title:  '🎯 Objectives',
        cat_core:               'Core',
        cat_supporting:         'Supporting',
        cat_detail:             'Detail',
        close_modal:            'Close',

        // ── Teaching view ─────────────────────────────────────────────
        turns_label:            'Turns:',
        concepts_label:         'Concepts:',
        relationships_label:    'Relationships:',
        tokens_label:           'Tokens:',
        end_session:            'Finish & Evaluate',
        message_placeholder:    'Type your message…',
        send:                   'Send',

        // ── Topic review ──────────────────────────────────────────────
        review_back:            '← Back',
        what_learned:           '📋 What the agent learned',
        duration_label:         'Duration:',
        min_unit:               'min',
        no_assessment:          'No evaluation available for this topic.',

        // ── Coverage display ──────────────────────────────────────────
        coverage_title:         '📊 Curriculum Coverage',
        coverage_overall:       'Overall coverage',
        coverage_core:          'Core objectives',
        coverage_detail:        '{achieved} achieved · {partial} partial · {not_covered} not covered',
        obj_achieved:           'Achieved',
        obj_partial:            'Partial',
        obj_not_covered:        'Not covered',

        // ── Confirm / Alert messages ──────────────────────────────────
        confirm_end:            'Finish and evaluate?\nThe agent will show what it learned.',
        session_complete_title: 'Evaluation ready!',
        stats_turns:            'Turns',
        stats_concepts:         'Concepts',
        stats_duration:         'Duration',
        stats_min:              'min',

        // ── Error messages ─────────────────────────────────────────────
        error_init:             'Failed to initialize app. Please refresh the page.',
        error_signin:           'Failed to sign in. Please try again.',
        error_create_session:   'Failed to create topic. Please try again.',
        error_load_sessions:    'Failed to load topics. Please try again.',
        error_load_session:     'Failed to load topic. Please try again.',
        error_end_session:      'Failed to generate evaluation. Please try again.',
        error_send_message:     'Sorry, I encountered an error. Please try again.',
        error_topic_required:   'Please enter a topic.',
        error_syllabus_required:'Please select a syllabus.',
        error_load_syllabi:     'Failed to load syllabi. Please try again.',

        // ── Token report ──────────────────────────────────────────────
        tokens_in:              'in',
        tokens_out:             'out',
        tokens_na:              '—',

        // ── Topic card actions ────────────────────────────────────────
        continue_teaching:      'Continue Teaching',
        view_learning:          'View',
        evaluate_btn:           'Evaluate',
        delete_btn:             'Delete',
        confirm_delete:         'Delete this topic? This cannot be undone.',
        error_delete_session:   'Failed to delete topic. Please try again.',
        session_active:         'In Progress',
        session_completed:      'Completed',

        // ── Token summary ──────────────────────────────────────────────
        token_report_title:     '📊 Token Usage',
        token_total_in:         'Input',
        token_total_out:        'Output',
        token_sessions_count:   'across {n} topic(s)',

        // ── Concepts panel ─────────────────────────────────────────────
        concepts_title:         '🧠 Concepts Learned',
        no_concepts:            'No concepts recorded yet.',

        // ── Review view ────────────────────────────────────────────────
        review_continue:        'Continue Teaching',

        // ── Assessment result modal ────────────────────────────────────
        assessment_pretitle:    '✅ Assessment ready',
        assessment_modal_view:  'View in detail →',
        assessment_modal_back:  'Back to my topics',

        // ── Inline delete confirmation ─────────────────────────────────
        confirm_delete_question:'Delete this topic?',
        confirm_delete_yes:     'Yes, delete',
        confirm_delete_cancel:  'Cancel',
    },

    es: {
        // ── App / Header ──────────────────────────────────────────────
        app_title:              'Agente Aprendiz',
        sign_out:               'Salir',
        lang_toggle_label:      'Idioma',

        // ── Login ──────────────────────────────────────────────────────
        login_title:            '🤖 Agente Aprendiz',
        login_tagline:          'Enseña a un agente de IA que parte sin saber nada',
        sign_in_google:         'Ingresar con Google',

        // ── Lista de temas ────────────────────────────────────────────
        teaching_sessions:      'Mis Temas de Enseñanza',
        new_session:            '+ Nuevo Tema',
        empty_sessions:         'Aún no tienes temas. ¡Empieza a enseñar algo!',
        loading_sessions:       'Cargando temas…',
        turns_unit:             'turnos',
        concepts_unit:          'conceptos',
        view:                   'Ver',
        continue_btn:           'Continuar',
        badge_free:             'Libre',
        badge_curricular:       'Curricular',

        // ── Nuevo Tema ────────────────────────────────────────────────
        create_session_title:   'Nuevo Tema de Enseñanza',
        back:                   '← Atrás',
        topic_label:            '¿Qué tema vas a enseñar?',
        topic_placeholder:      'ej. Cómo hacer pizza, Fotosíntesis…',
        language_label:         'Idioma de la conversación (respuestas del agente)',
        lang_en:                'English',
        lang_es:                'Español',
        session_type_label:     'Modalidad',
        session_type_free:      '🔓 Tema libre — enseña cualquier cosa',
        session_type_curricular:'📚 Curricular — guiado por objetivos de aprendizaje',
        syllabus_label:         'Selecciona un programa',
        syllabus_placeholder:   '— Elige un programa —',
        preview_objectives_btn: 'Ver objetivos',
        start_teaching:         'Comenzar a Enseñar',

        // ── Objectives modal ──────────────────────────────────────────
        objectives_title:       'Objetivos de Aprendizaje',
        big_ideas_title:        '💡 Ideas Principales',
        objectives_list_title:  '🎯 Objetivos',
        cat_core:               'Central',
        cat_supporting:         'Apoyo',
        cat_detail:             'Detalle',
        close_modal:            'Cerrar',

        // ── Vista de enseñanza ────────────────────────────────────────
        turns_label:            'Turnos:',
        concepts_label:         'Conceptos:',
        relationships_label:    'Relaciones:',
        tokens_label:           'Tokens:',
        end_session:            'Finalizar y Evaluar',
        message_placeholder:    'Escribe tu mensaje…',
        send:                   'Enviar',

        // ── Vista de revisión ─────────────────────────────────────────
        review_back:            '← Atrás',
        what_learned:           '📋 Lo que aprendió el agente',
        duration_label:         'Duración:',
        min_unit:               'min',
        no_assessment:          'Este tema no tiene evaluación disponible.',

        // ── Cobertura curricular ──────────────────────────────────────
        coverage_title:         '📊 Cobertura Curricular',
        coverage_overall:       'Cobertura general',
        coverage_core:          'Objetivos centrales',
        coverage_detail:        '{achieved} logrados · {partial} parciales · {not_covered} sin cubrir',
        obj_achieved:           'Logrado',
        obj_partial:            'Parcial',
        obj_not_covered:        'Sin cubrir',

        // ── Confirmaciones y alertas ──────────────────────────────────
        confirm_end:            '¿Finalizar y evaluar?\nEl agente demostrará lo que aprendió de este tema.',
        session_complete_title: '¡Evaluación lista!',
        stats_turns:            'Turnos',
        stats_concepts:         'Conceptos',
        stats_duration:         'Duración',
        stats_min:              'min',

        // ── Mensajes de error ─────────────────────────────────────────
        error_init:             'Error al iniciar la aplicación. Por favor recarga la página.',
        error_signin:           'Error al ingresar. Por favor intenta de nuevo.',
        error_create_session:   'Error al crear el tema. Por favor intenta de nuevo.',
        error_load_sessions:    'Error al cargar los temas. Por favor intenta de nuevo.',
        error_load_session:     'Error al cargar el tema. Por favor intenta de nuevo.',
        error_end_session:      'Error al generar la evaluación. Por favor intenta de nuevo.',
        error_send_message:     'Lo siento, ocurrió un error. Por favor intenta de nuevo.',
        error_topic_required:   'Por favor ingresa un tema.',
        error_syllabus_required:'Por favor selecciona un programa.',
        error_load_syllabi:     'Error al cargar los programas. Por favor intenta de nuevo.',

        // ── Token report ──────────────────────────────────────────────
        tokens_in:              'entrada',
        tokens_out:             'salida',
        tokens_na:              '—',

        // ── Acciones en tarjetas de tema ──────────────────────────────
        continue_teaching:      'Continuar Enseñando',
        view_learning:          'Ver',
        evaluate_btn:           'Evaluar',
        delete_btn:             'Eliminar',
        confirm_delete:         '¿Eliminar este tema? Esta acción no se puede deshacer.',
        error_delete_session:   'Error al eliminar el tema. Por favor intenta de nuevo.',
        session_active:         'En Progreso',
        session_completed:      'Completado',

        // ── Resumen de tokens ─────────────────────────────────────────
        token_report_title:     '📊 Uso de Tokens',
        token_total_in:         'Entrada',
        token_total_out:        'Salida',
        token_sessions_count:   'en {n} tema(s)',

        // ── Panel de conceptos ────────────────────────────────────────
        concepts_title:         '🧠 Conceptos Aprendidos',
        no_concepts:            'Aún no hay conceptos registrados.',

        // ── Vista de revisión ─────────────────────────────────────────
        review_continue:        'Continuar Enseñando',

        // ── Modal de resultado de evaluación ──────────────────────────
        assessment_pretitle:    '✅ Evaluación lista',
        assessment_modal_view:  'Ver en detalle →',
        assessment_modal_back:  'Volver a mis temas',

        // ── Confirmación de eliminación inline ────────────────────────
        confirm_delete_question:'¿Eliminar este tema?',
        confirm_delete_yes:     'Sí, eliminar',
        confirm_delete_cancel:  'Cancelar',
    }
};

// ── State ──────────────────────────────────────────────────────────────────
let currentLang = localStorage.getItem('app_language') || 'es';

// ── Public API ─────────────────────────────────────────────────────────────

/** Translate a key in the current language */
export function t(key) {
    return translations[currentLang]?.[key]
        ?? translations['en']?.[key]
        ?? key;
}

/** Get current language code */
export function getLang() {
    return currentLang;
}

/**
 * Switch the app language and re-render all data-i18n elements.
 * Persists to localStorage.
 */
export function setLanguage(lang) {
    if (!translations[lang]) return;
    currentLang = lang;
    localStorage.setItem('app_language', lang);
    applyTranslations();
}

/**
 * Walk all elements with data-i18n and update their text (or attribute).
 * Also syncs the language toggle buttons and the html[lang] attribute.
 *
 * data-i18n="key"              → sets element.textContent
 * data-i18n="key" data-i18n-attr="placeholder" → sets element.placeholder
 */
export function applyTranslations() {
    document.querySelectorAll('[data-i18n]').forEach(el => {
        const key  = el.getAttribute('data-i18n');
        const attr = el.getAttribute('data-i18n-attr');
        const text = t(key);
        if (attr) {
            el.setAttribute(attr, text);
        } else {
            el.textContent = text;
        }
    });

    // Highlight active language button
    document.querySelectorAll('.lang-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.lang === currentLang);
    });

    document.documentElement.lang = currentLang;
}
